import { X, ChevronRight } from 'lucide-react';
import type { GroupWithMembers } from '@/api/groups';
import { useTranslation } from 'react-i18next';

interface GroupPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onPick: (groupId: string) => void;
  groups: GroupWithMembers[];
}

export function GroupPickerDialog({ open, onClose, onPick, groups }: GroupPickerDialogProps) {
  const { t } = useTranslation();
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md max-h-full overflow-y-auto rounded-xl bg-surface-primary p-6 shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">{t('expenses:screen.addExpense')}</h2>
            <p className="mt-0.5 text-sm text-text-tertiary">{t('web:groupPickerDialog.chooseAGroupToAddThe')}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-text-quaternary hover:text-text-secondary"
            aria-label={t('common:button.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {groups.length > 0 && (
          <ul className="max-h-96 divide-y divide-border-tertiary overflow-y-auto rounded-lg ring-1 ring-border-primary">
            {groups.map(group => (
              <li key={group.id}>
                <button
                  onClick={() => onPick(group.id)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-hover transition-colors"
                >
                  <span
                    className="h-8 w-8 shrink-0 rounded-full"
                    style={{ backgroundColor: group.color ?? '#6366F1' }}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-text-primary">
                      {group.name}
                    </span>
                    <span className="block text-xs text-text-tertiary">
                      {group.members.length} {group.members.length === 1 ? 'member' : 'members'}
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-text-quaternary" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
